import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { I18nProvider } from "@/i18n/I18nContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { UniverseProvider } from "@/context/UniverseContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";

import Home from "@/pages/public/Home";
import UniversePage from "@/pages/public/UniversePage";
import PublicSchedule from "@/pages/public/PublicSchedule";
import Pricing from "@/pages/public/Pricing";
import Team from "@/pages/public/Team";
import Contact from "@/pages/public/Contact";
import GymCoronado from "@/pages/public/GymCoronado";
import PilatesCoronado from "@/pages/public/PilatesCoronado";
import CrossfitCoronado from "@/pages/public/CrossfitCoronado";
import FunctionalMovementCoronado from "@/pages/public/FunctionalMovementCoronado";
import FunctionalFitnessCenter from "@/pages/public/FunctionalFitnessCenter";
import NotFound from "@/pages/public/NotFound";
import Horarios from "@/pages/public/Horarios";
import Blog from "@/pages/public/Blog";
import BlogPost from "@/pages/public/BlogPost";
import FAQ_Page from "@/pages/public/FAQ";
import Legal from "@/pages/public/Legal";
import { Login, Register, Forgot, ResetPassword } from "@/pages/auth/Auth";
import { AuthCallback } from "@/pages/auth/AuthCallback";
import { Dashboard } from "@/pages/app/Dashboard";
import { BookSchedule } from "@/pages/app/BookSchedule";
import { Credits } from "@/pages/app/Credits";
import { Profile } from "@/pages/app/Profile";
import { Wod } from "@/pages/app/Wod";
import { MyProgram } from "@/pages/app/MyProgram";
import { PRWall } from "@/pages/app/PRWall";
import { Progress } from "@/pages/app/Progress";
import { AdminDashboard, AdminClients, AdminPayments } from "@/pages/admin/AdminCore";
import { AdminClasses, AdminProducts, AdminSettings } from "@/pages/admin/AdminManage";
import { AdminWod } from "@/pages/admin/AdminWod";
import { AdminVideos } from "@/pages/admin/AdminVideos";
import { AdminPrograms } from "@/pages/admin/AdminPrograms";
import { CoachDashboard } from "@/pages/coach/CoachDashboard";

function AppRoutes() {
  const location = useLocation();
  // Google OAuth: process session_id in URL fragment before anything else
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/fitness" element={<UniversePage universe="fitness" />} />
      <Route path="/pilates" element={<UniversePage universe="pilates" />} />
      <Route path="/schedule" element={<PublicSchedule />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/team" element={<Team />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/gym-coronado" element={<GymCoronado />} />
      <Route path="/pilates-coronado" element={<PilatesCoronado />} />
      <Route path="/crossfit-coronado" element={<CrossfitCoronado />} />
      <Route path="/functional-movement-coronado" element={<FunctionalMovementCoronado />} />
      <Route path="/functional-fitness-center-coronado" element={<FunctionalFitnessCenter />} />
      <Route path="/horarios" element={<Horarios />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/faq" element={<FAQ_Page />} />
      <Route path="/legal/:slug" element={<Legal />} />

      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot" element={<Forgot />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      <Route path="/app" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/app/schedule" element={<ProtectedRoute><BookSchedule /></ProtectedRoute>} />
      <Route path="/app/credits" element={<ProtectedRoute><Credits /></ProtectedRoute>} />
      <Route path="/app/wod" element={<ProtectedRoute><Wod /></ProtectedRoute>} />
      <Route path="/app/program" element={<ProtectedRoute><MyProgram /></ProtectedRoute>} />
      <Route path="/app/prs" element={<ProtectedRoute><PRWall /></ProtectedRoute>} />
      <Route path="/app/progress" element={<ProtectedRoute><Progress /></ProtectedRoute>} />
      <Route path="/app/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />

      <Route path="/coach" element={<ProtectedRoute roles={["coach", "admin"]}><CoachDashboard /></ProtectedRoute>} />

      <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/clients" element={<ProtectedRoute roles={["admin"]}><AdminClients /></ProtectedRoute>} />
      <Route path="/admin/classes" element={<ProtectedRoute roles={["admin"]}><AdminClasses /></ProtectedRoute>} />
      <Route path="/admin/wod" element={<ProtectedRoute roles={["admin", "coach"]}><AdminWod /></ProtectedRoute>} />
      <Route path="/admin/programs" element={<ProtectedRoute roles={["admin", "coach"]}><AdminPrograms /></ProtectedRoute>} />
      <Route path="/admin/videos" element={<ProtectedRoute roles={["admin", "coach"]}><AdminVideos /></ProtectedRoute>} />
      <Route path="/admin/products" element={<ProtectedRoute roles={["admin"]}><AdminProducts /></ProtectedRoute>} />
      <Route path="/admin/payments" element={<ProtectedRoute roles={["admin"]}><AdminPayments /></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute roles={["admin"]}><AdminSettings /></ProtectedRoute>} />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <I18nProvider>
      <SettingsProvider>
        <ThemeProvider>
          <UniverseProvider>
            <AuthProvider>
              <BrowserRouter>
                <AppRoutes />
                <Toaster position="top-center" richColors />
              </BrowserRouter>
            </AuthProvider>
          </UniverseProvider>
        </ThemeProvider>
      </SettingsProvider>
    </I18nProvider>
  );
}

export default App;
